
import { useState } from 'react';
import { 
  updateSubscriptionService, 
  addSubscriptionService, 
  removeSubscriptionService 
} from '@/services/subscriptionServiceService';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { getServices } from '@/services/serviceService';

export function useSubscriptionServices(subscriptionId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  // Add query to fetch all available services
  const { data: allServices = [] } = useQuery({
    queryKey: ['services'],
    queryFn: getServices,
    // Disable auto-refetching to prevent re-render loops
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const updateService = async (serviceData: any) => {
    setIsLoading(true);
    try {
      console.log('Updating service data:', JSON.stringify(serviceData, null, 2));
      
      // Make sure the id field is correctly included
      if (!serviceData.id) {
        throw new Error('Missing id field required for update');
      }

      console.log(`Service ID for update: ${serviceData.id}`);
      
      // Format dates properly
      const formattedData = {
        ...serviceData,
        registration_date: formatDateIfNeeded(serviceData.registration_date),
        activation_date: formatDateIfNeeded(serviceData.activation_date),
        termination_date: formatDateIfNeeded(serviceData.termination_date),
        cancellation_date: formatDateIfNeeded(serviceData.cancellation_date),
      };
      
      console.log('Formatted update data:', JSON.stringify(formattedData, null, 2));
      
      // Pass the data to the service function
      await updateSubscriptionService(formattedData);
      
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['subscription', subscriptionId] });
      queryClient.invalidateQueries({ queryKey: ['subscription-services', subscriptionId] });
      
      toast.success('Service updated successfully');
      return true;
    } catch (error) {
      console.error('Error updating service:', error);
      toast.error('Failed to update service');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const addService = async (serviceData: any) => {
    setIsLoading(true);
    try {
      console.log('Adding service data from hook:', JSON.stringify(serviceData, null, 2));
      
      // Format dates properly
      const formattedData = {
        ...serviceData,
        registration_date: formatDateIfNeeded(serviceData.registration_date),
        activation_date: formatDateIfNeeded(serviceData.activation_date),
        termination_date: formatDateIfNeeded(serviceData.termination_date),
        cancellation_date: formatDateIfNeeded(serviceData.cancellation_date),
        subscription_id: subscriptionId, // Ensure subscription_id is set
      };
      
      console.log('Formatted service data:', JSON.stringify(formattedData, null, 2));
      
      const result = await addSubscriptionService(formattedData);
      
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['subscription', subscriptionId] });
      queryClient.invalidateQueries({ queryKey: ['subscription-services', subscriptionId] });
      
      return result;
    } catch (error) {
      console.error('Error adding service:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const removeService = async (serviceId: string, userEmail: string) => {
    setIsLoading(true);
    try {
      await removeSubscriptionService(subscriptionId, serviceId, userEmail);
      
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['subscription', subscriptionId] });
      queryClient.invalidateQueries({ queryKey: ['subscription-services', subscriptionId] });
      
      return true;
    } catch (error) {
      console.error('Error removing service:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Helper function to format dates
  const formatDateIfNeeded = (date: Date | string | null): string | null => {
    if (!date) return null;
    if (date instanceof Date) {
      return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
    }
    if (typeof date === 'string') {
      // If it's already formatted as YYYY-MM-DD
      if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return date;
      }
      // Otherwise parse and format
      try {
        return new Date(date).toISOString().split('T')[0];
      } catch (e) {
        console.error('Invalid date string:', date);
        return null;
      }
    }
    return null;
  };

  return {
    updateService,
    addService,
    removeService,
    isLoading,
    allServices
  };
}
